import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail,
  Phone,
  Send,
  CheckCircle,
  Calendar,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Monitor
} from 'lucide-react';
import { PLAY_STORE_URL, WEB_APP_URL } from '../config/urls';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Send form data to contact.php on XAMPP server
      // Update this URL if your XAMPP runs on a different port (e.g., http://localhost:8080/contact.php)
      
      //const phpUrl='http://localhost/contact.php'
      const phpUrl = 'https://wellnessproapp.com/contact.php';  //Enable to launch on website
      
      const response = await fetch(phpUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          business: formData.business,
          message: formData.message
        })
      });

      const data = await response.json();

      if (data.success) {
        // Success - show success message
        setIsSubmitted(true);
        setSubmissionId(data.submission_id);
        setEmailSent(data.email_sent || false);
        setEmailError(data.email_error || null);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          business: '',
          message: ''
        });
      } else {
        // Error from server
        setError(data.message || 'An error occurred. Please try again.');
        setIsLoading(false);
      }
    } catch (err) {
      // Network or other error
      setError('Unable to connect to server. Please check your internet connection and try again.');
      setIsLoading(false);
    }
  };

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqData = [
    {
      question: "How long is the free trial?",
      answer: "Our free trial lasts for 60 days with full access to all features. No credit card required to start."
    },
    {
      question: "What happens after the trial ends?",
      answer: "After your trial, you can choose from our flexible pricing plans. We'll help you migrate your data seamlessly."
    },
    {
      question: "Do you provide training and support?",
      answer: "Yes! We provide comprehensive training for your team and 24/7 customer support throughout your journey."
    },
    {
      question: "Can I use the app offline?",
      answer: "Absolutely! Our mobile app works offline and syncs your data when you're back online."
    },
    {
      question: "Is my data secure?",
      answer: "Yes, we use enterprise-grade security with encrypted data storage and regular backups to keep your information safe."
    },
    {
      question: "Can I customize the app for my spa?",
      answer: "Definitely! Wellness Pro offers extensive customization options including branding, services, pricing, and workflows."
    }
  ];

  const contactInfo = [
    {
      icon: <Phone size={24} />,
      title: 'Call Us',
      details: ['+91 9311252321'],
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: <Mail size={24} />,
      title: 'Email Us',
      details: ['support@wellnessproapp.com'],
      color: 'from-blue-500 to-cyan-500'
    }
  ];

  const trialBenefits = [
    '60-day free trial with full access',
    'No credit card required',
    'Personal onboarding session',
    '24/7 customer support',
    'Data migration assistance',
    'Training for your team'
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-slate-50 to-slate-200">
        <div className="container">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-large text-gradient mb-4">
              Start Your Free Trial Today
            </h1>
            <p className="text-small text-gray-600 max-w-2xl mx-auto">
              Ready to transform your spa business? Get in touch with us to start your free trial and see how Wellness Pro can revolutionize your operations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-6 lg:py-8 bg-white">
        <div className="container max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Contact Form */}
            <motion.div
              className="bg-white rounded-2xl p-8 lg:p-10 shadow-card border border-gray-200"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-10">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Get Started with Your Free Trial</h2>
                <p className="text-lg text-gray-600 leading-relaxed max-w-lg mx-auto">
                  Fill out the form below and our team will get back to you within 24 hours to set up your free trial.
                </p>
              </div>

              {error && (
                <motion.div
                  className="text-center p-6 bg-gradient-to-br from-red-50 to-red-100 rounded-2xl border-2 border-red-200 mb-6"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-red-700 font-medium">{error}</p>
                </motion.div>
              )}

              {isSubmitted ? (
                <motion.div
                  className="text-center p-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-green-200"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
                  <h3 className="text-3xl font-bold text-green-800 mb-4">Thank You!</h3>
                  <p className="text-green-700 leading-relaxed mb-6 text-lg">
                    We've received your information successfully! Our team will contact you within 24 hours to set up your free trial.
                  </p>
                  {submissionId && (
                    <p className="text-green-600 text-sm mb-4">
                      Submission ID: #{submissionId}
                    </p>
                  )}
                  {emailSent && (
                    <motion.div
                      className="mb-6 p-4 bg-green-100 border border-green-300 rounded-xl"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex items-center gap-3 justify-center">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <p className="text-green-700 font-medium">
                          Your Query has been successfully submitted, our team will reach out to you as soon as possible
                        </p>
                      </div>
                    </motion.div>
                  )}
                  {emailError && (
                    <motion.div
                      className="mb-6 p-4 bg-yellow-100 border border-yellow-300 rounded-xl"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex items-center gap-3 justify-center">
                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                        <p className="text-yellow-700 text-sm">
                          Note: {emailError}
                        </p>
                      </div>
                    </motion.div>
                  )}
                  <div className="mt-8 pt-6 border-t border-green-200">
                    <p className="text-green-700 font-semibold mb-4">Get started on mobile or web</p>
                    <div className="flex flex-wrap gap-4">
                      <a
                        href={WEB_APP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold px-5 py-3 rounded-xl hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/25 transition-all duration-300"
                      >
                        <Monitor className="w-5 h-5" />
                        Launch Web App
                      </a>
                      <a
                        href={PLAY_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-cyan-600 hover:via-blue-600 hover:to-indigo-700 transition-all duration-300 hover:scale-105 shadow-lg shadow-cyan-500/40"
                      >
                        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                        </svg>
                        <div className="flex flex-col items-start">
                          <span className="text-[10px] leading-tight">GET IT ON</span>
                          <span className="text-lg font-bold leading-tight">Google Play</span>
                        </div>
                      </a>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <form className="space-y-8" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="flex flex-col">
                      <label htmlFor="name" className="font-semibold text-gray-700 mb-2 text-sm">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter your full name"
                        className="px-4 py-3 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white focus:outline-none focus:border-primary-500 focus:shadow-lg focus:shadow-primary-500/10"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label htmlFor="email" className="font-semibold text-gray-700 mb-2 text-sm">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter your email address"
                        className="px-4 py-3 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white focus:outline-none focus:border-primary-500 focus:shadow-lg focus:shadow-primary-500/10"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="flex flex-col">
                      <label htmlFor="phone" className="font-semibold text-gray-700 mb-2 text-sm">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter your phone number"
                        className="px-4 py-3 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white focus:outline-none focus:border-primary-500 focus:shadow-lg focus:shadow-primary-500/10"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label htmlFor="business" className="font-semibold text-gray-700 mb-2 text-sm">Spa/Business Name *</label>
                      <input
                        type="text"
                        id="business"
                        name="business"
                        value={formData.business}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter your business name"
                        className="px-4 py-3 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white focus:outline-none focus:border-primary-500 focus:shadow-lg focus:shadow-primary-500/10"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label htmlFor="message" className="font-semibold text-gray-700 mb-2 text-sm">Tell us about your business (Optional)</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Share details about your spa, number of rooms, current challenges, etc."
                      className="px-4 py-3 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white focus:outline-none focus:border-primary-500 focus:shadow-lg focus:shadow-primary-500/10 resize-vertical min-h-24"
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="btn btn-primary btn-large w-full mt-8 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2" size={20} />
                        Submit
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Info & Benefits */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              {/* Contact Info Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      {info.title === 'Call Us' ? (
                        <a
                          href="tel:+919311252321"
                          className={`w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-r ${info.color} shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer`}
                          aria-label="Call Us"
                        >
                          <div className="w-8 h-8 text-white">
                            {info.icon}
                          </div>
                        </a>
                      ) : (
                        <a
                          href="mailto:support@wellnessproapp.com"
                          className={`w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-r ${info.color} shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer`}
                          aria-label="Email Us"
                        >
                          <div className="w-8 h-8 text-white">
                            {info.icon}
                          </div>
                        </a>
                      )}
                      {info.title === 'Call Us' && (
                        <a
                          href="https://wa.me/919311252321"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-r from-green-500 to-emerald-500 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer"
                          aria-label="WhatsApp"
                        >
                          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                          </svg>
                        </a>
                      )}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{info.title}</h3>
                    <div className="space-y-2">
                      {info.details.map((detail, detailIndex) => (
                        <p key={detailIndex} className="text-gray-600 leading-relaxed">{detail}</p>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Trial Benefits */}
              <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-8 lg:p-10 border border-primary-200">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                    <Calendar className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                    What's Included in Your Free Trial
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {trialBenefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-white/50 hover:shadow-md transition-all duration-300"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 font-medium text-sm lg:text-base">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section py-4 lg:py-6 gradient-bg">
        <div className="container max-w-7xl">
          <motion.div
            className="max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-6">
              <h2 className="text-large text-white mb-2">
                Frequently Asked Questions
              </h2>
              <p className="text-small text-white/90 max-w-2xl mx-auto">
                Get answers to common questions about Wellness Pro and our free trial.
              </p>
            </div>

            <div className="space-y-6">
              {faqData.map((faq, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <button
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all duration-300"
                    onClick={() => toggleFAQ(index)}
                  >
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    <motion.div
                      animate={{ rotate: openFAQ === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex-shrink-0"
                    >
                      {openFAQ === index ? (
                        <ChevronUp className="w-5 h-5 text-primary-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </motion.div>
                  </button>
                  
                  <AnimatePresence>
                    {openFAQ === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-4">
                          <div className="h-px bg-gray-200 mb-4"></div>
                          <p className="text-gray-700 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
